import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './HomePage.module.css';

// Hero slides: emphasize workshops and the cart build
const slidesData = [
  {
    id: 's1',
    title: 'Live AI Robotics Workshops for Teens',
    subtitle: 'Every Saturday • 9:30 AM–12 PM • Hands-on engineering • Build real robots',
    cta: 'Sign Up for Next Workshop',
    href: 'https://www.eventbrite.com/e/ai-robotics-workshop-for-teens-hands-on-engineering-experience-weekly-tickets-1990292587707',
    external: true,
    // SWAP: Replace roboclass.png with a photo of teens in your workshop actually building/wiring
    image: '/images/roboclass.png',
    alt: 'Teens building and wiring a robot in a hands-on workshop'
  },
  {
    id: 's2',
    title: 'The ChronoTech Theme Park Robot — Built Live in Our Lab',
    subtitle: 'Watch us engineer an ice cream dispensing robot. See real robotics, AI, and mechanics in action.',
    cta: 'See the Build',
    href: '/innovator',
    // SWAP: Replace innovator.png with a photo of your ice cream cart/theme park robot
    image: '/images/innovator.png',
    alt: 'ChronoTech ice cream robot cart with modular arcade game platform'
  },
  {
    id: 's3',
    title: 'AI Robotics Guides: Circuits, Coding, and Autonomy',
    subtitle: 'Step-by-step tutorials—sensors, decision logic, and real projects.',
    cta: 'Start Learning',
    href: '/guides',
    // KEEP: This can stay as robokids.png or swap for a guide/tutorial screenshot
    image: '/images/robokids.png',
    alt: 'Students following a robotics guide on a tablet'
  }
];

const valueStripData = [
  {
    title: 'Learn Real Robotics',
    description: 'Sensors, motors, electronics, and Arduino coding. Build projects that actually work.'
  },
  {
    title: 'Hands-On Every Week',
    description: 'Saturday workshops with live instruction. Take your robot home and keep experimenting.'
  },
  {
    title: 'Beginner-Friendly & Safe',
    description: 'Ages 13+, no experience needed. Supportive instructors and a creative learning environment.'
  }
];

const featuresData = [
  {
    // SWAP: Replace roboclass.png with workshop in-action photo (teens soldering, wiring, etc.)
    image: '/images/roboclass.png',
    alt: 'Live AI Robotics Workshop',
    title: 'Weekly Robotics Workshops',
    description: 'Every Saturday, teens assemble, wire, and code their own robots. Learn electronics, mechanics, and Arduino programming from instructors who build real projects.',
    cta: 'Sign Up Now',
    href: 'https://www.eventbrite.com/e/ai-robotics-workshop-for-teens-hands-on-engineering-experience-weekly-tickets-1990292587707',
    external: true
  },
  {
    // SWAP: Replace innovator.png with your ice cream robot/theme park robot cart
    image: '/images/innovator.png',
    alt: 'ChronoTech Theme Park Robot',
    title: 'The Theme Park Robot',
    description: 'We\'re building a modular entertainment robot for events and parties. Arcade games, ice cream dispensing, and AI control. Watch the engineering in real time.',
    cta: 'See the Build',
    href: '/innovator'
  },
  {
    // KEEP: innovator2.png or swap for a mentor/founder mentorship photo
    image: '/images/innovator2.png',
    alt: 'ChronoTech Innovator Program',
    title: 'Innovator to CEO Program',
    description: 'Mentorship for young founders. Take your idea from prototype to pitch, learn product strategy, and grow as a leader.',
    cta: 'Learn More',
    href: '/innovator'
  }
];

// Reusable FeatureCard Component
function FeatureCard({ image, alt, title, description, cta, href, external }) {
    const navigate = useNavigate();
    const handleClick = () => {
        if (external) {
            window.open(href, '_blank');
        } else {
            navigate(href);
        }
    };
    return (
        <div className={styles.featureCard}>
            <img src={image} alt={alt} />
            <h3>{title}</h3>
            <p>{description}</p>
            <button
                className={styles.secondaryBtn}
                onClick={handleClick}
                aria-label={`${cta} for ${title}`}
            >
                {cta}
            </button>
        </div>
    );
}

export default function HomePage() {
    const navigate = useNavigate();
    const [index, setIndex] = useState(0);
    const timerRef = useRef(null);
    const slideCount = slidesData.length;

    // Function to reset and start the auto-play timer
    const startTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        timerRef.current = setInterval(() => {
            setIndex(prev => (prev + 1) % slideCount);
        }, 7000);
    };

    useEffect(() => {
        startTimer();
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [slideCount]);

    const handleGoTo = (i) => {
        setIndex(i);
        startTimer();
    };

    const handleNext = () => {
        setIndex((index + 1) % slideCount);
        startTimer();
    };

    const handlePrev = () => {
        setIndex((index - 1 + slideCount) % slideCount);
        startTimer();
    };

    return (
        <div className={styles.homeContainer}>
            {/* HERO SLIDER */}
            <section className={styles.hero}>
                <div className={styles.slider}>
                    <div className={styles.track} style={{ transform: `translateX(-${index * 100}%)` }}>
                        {slidesData.map(s => (
                            <article className={styles.slide} key={s.id}>
                                <div className={styles.slideContent}>
                                    <h1 className={styles.heroTitle}>{s.title}</h1>
                                    <p className={styles.heroSubtitle}>{s.subtitle}</p>
                                    <button 
                                        className={styles.primaryBtn} 
                                        onClick={() => s.external ? window.open(s.href, '_blank') : navigate(s.href)}
                                    >
                                        {s.cta}
                                    </button>
                                </div>
                                <div className={styles.imageWrap}>
                                    <img src={s.image} alt={s.alt} className={styles.slideImg} />
                                </div>
                            </article>
                        ))}
                    </div>

                    <button aria-label="Previous" className={styles.arrowLeft} onClick={handlePrev}><span>‹</span></button>
                    <button aria-label="Next" className={styles.arrowRight} onClick={handleNext}><span>›</span></button>

                    <div className={styles.dots}>
                        {slidesData.map((_, i) => (
                            <button
                                key={i}
                                className={`${styles.dot} ${i === index ? styles.dotActive : ''}`}
                                aria-label={`Go to slide ${i + 1}`}
                                onClick={() => handleGoTo(i)}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* VALUE STRIP */}
            <section className={styles.valueStrip}>
                {valueStripData.map((card, idx) => (
                    <div className={styles.valueCard} key={idx}>
                        <h4>{card.title}</h4>
                        <p>{card.description}</p>
                    </div>
                ))}
            </section>

            {/* FEATURES SECTION */}
            <section className={styles.features}>
                {featuresData.map((feature, idx) => (
                    <FeatureCard key={idx} {...feature} />
                ))}
            </section>
        </div>
    );
}